import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Crown, Settings2, BarChart3, TrendingUp, Compass, Lightbulb } from "lucide-react";
import ApplyModal from "@/components/ApplyModal";

const CATEGORIES = [
  {
    id: "ceo-md",
    icon: Crown,
    title: "CEO / Managing Director",
    body:
      "For founders, scale-stage CEOs and seasoned MDs ready to lead the next chapter — through growth, restructure, or transition.",
  },
  {
    id: "coo",
    icon: Settings2,
    title: "COO / Operations Leader",
    body:
      "Operational leaders who codify how a business runs at scale — building the rhythms, infrastructure and team to support what comes next.",
  },
  {
    id: "cco",
    icon: BarChart3,
    title: "CCO / Commercial Leader",
    body:
      "Commercial directors and CCOs who unify revenue, marketing and partnerships into a single, deliberate growth motion.",
  },
  {
    id: "cro",
    icon: TrendingUp,
    title: "CRO / Revenue Leader",
    body:
      "Revenue leaders engineered for B2B SaaS and founder-led commercial organisations — accountable for predictable, repeatable growth.",
  },
  {
    id: "vp-growth",
    icon: Compass,
    title: "VP Growth",
    body:
      "Growth operators who own acquisition, activation and monetisation — at home with PLG, demand generation and modern GTM.",
  },
  {
    id: "cpo",
    icon: Lightbulb,
    title: "Chief Product Officer / CPO",
    body:
      "Product strategy, innovation and commercial product leadership.",
  },
];

export default function TalentNetwork() {
  const [activeCategory, setActiveCategory] = useState(null);

  return (
    <section
      id="opportunities"
      data-testid="talent-network-section"
      className="relative bg-[#0E2750] py-24 md:py-36 border-t border-white/[0.06]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-mono-eyebrow text-[#C9A876]"
            >
              ◆ Leadership Talent Network
            </motion.span>
            <motion.h2
              data-testid="talent-network-heading"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white"
            >
              Considered for what comes{" "}
              <span className="font-display-italic text-[#C9A876]">next</span>.
            </motion.h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-base md:text-lg text-slate-300/80 leading-relaxed">
              ArcPoint is building a curated network of exceptional senior
              operators, commercial leaders and founders across the UK
              market. Members may be contacted for confidential
              opportunities, strategic introductions, market conversations
              and future leadership mandates.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.article
                key={c.id}
                data-testid={`category-card-${c.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="group relative bg-[#0E2750] p-9 md:p-10 flex flex-col hover:bg-white/[0.025] transition-colors duration-700"
              >
                <div className="flex items-start justify-between mb-9">
                  <Icon size={26} strokeWidth={1.1} className="text-[#C9A876]" />
                  <span className="font-mono-eyebrow text-slate-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-white tracking-tight leading-tight mb-4">
                  {c.title}
                </h3>
                <p className="text-sm md:text-base text-slate-300/80 leading-relaxed mb-10 flex-1">
                  {c.body}
                </p>
                <button
                  data-testid={`category-register-${c.id}`}
                  onClick={() => setActiveCategory(c)}
                  className="self-start inline-flex items-center gap-2 px-5 py-2.5 border border-[#C9A876]/55 text-[#C9A876] hover:bg-[#C9A876] hover:text-[#0E2750] text-xs uppercase tracking-[0.2em] font-medium transition-all duration-500"
                >
                  Register Interest
                  <ArrowUpRight size={13} strokeWidth={1.5} />
                </button>
              </motion.article>
            );
          })}
        </div>

        <p className="mt-12 text-xs uppercase tracking-[0.25em] text-slate-400/80 text-center">
          Confidential · Founder-reviewed · UK-focused executive search
        </p>
      </div>

      <ApplyModal job={activeCategory} onClose={() => setActiveCategory(null)} category />
    </section>
  );
}
